/**
 * ChatInput Component
 * 
 * Input area with text field, audio recorder, and send button.
 * Handles keyboard shortcuts and audio recording state.
 */

import { useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AudioRecorder } from '@/components/AudioRecorder';

interface ChatInputProps {
  value: string;
  isLoading: boolean;
  isConnected: boolean;
  isRecording: boolean;
  onChange: (value: string) => void;
  onSend: (message: string) => void;
  onSendAudio: (audio: string) => void;
  onRecordingChange: (recording: boolean) => void;
  onShowMessage: (from: string, message: string) => void;
}

export function ChatInput({
  value,
  isLoading,
  isConnected,
  isRecording,
  onChange,
  onSend,
  onSendAudio,
  onRecordingChange,
  onShowMessage,
}: ChatInputProps) {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value.trim() && !isLoading && isConnected) {
      onSend(value.trim());
      onChange('');
    }
  };

  const isDisabled = isLoading || !isConnected;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="p-4 bg-white border-t border-gray-200">
      <form onSubmit={handleSubmit} className="flex gap-2 items-center">
        {!isRecording && (
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isConnected ? "Quisiera más info sobre la cursada..." : "Bot no disponible"}
            disabled={isDisabled}
            className="flex-1"
          />
        )}

        {isRecording && (
          <div className="flex-1" />
        )}

        <AudioRecorder
          isLoading={isDisabled}
          isRecording={isRecording}
          onRecordingChange={onRecordingChange}
          onSendAudio={onSendAudio}
          onShowMessage={onShowMessage}
        />

        {!isRecording && (
          <Button
            type="submit"
            size="icon"
            disabled={!value.trim() || isDisabled}
            aria-label="Enviar mensaje"
          >
            <Send className="h-5 w-5" />
          </Button>
        )}
      </form>
    </div>
  );
}

